import { useState, FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";

export default function PartnershipPage() {
  const [searchParams] = useSearchParams();
  const preselectedType = searchParams.get("type") || "";
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError("");

    const form = e.currentTarget;
    const formData = new FormData(form);

try {
  formData.append("access_key", "ae87c59e-785a-4e5a-b60d-36658da808b9");
  formData.append("subject", "New AKTEN INC Partnership Request");
  formData.append("from_name", "AKTEN INC Website");

  const response = await fetch("https://api.web3forms.com/submit", {
    method: "POST",
    body: formData,
  });

  const result = await response.json();

  if (response.ok && result.success) {
    setIsSubmitted(true);
    form.reset();
  } else {
    setError(result.message || "Something went wrong. Please try again.");
  }
} catch {
  setError("Network error. Please check your connection and try again.");
} finally {
  setIsSubmitting(false);
}
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center px-4 py-16">
        <div className="max-w-lg w-full bg-slate-900/80 border border-white/10 rounded-3xl shadow-2xl p-8 md:p-10 text-center">
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <i className="ri-check-line text-green-400 text-4xl"></i>
          </div>
          <h2 className="text-2xl font-bold mb-4">Thank You!</h2>
          <p className="text-white/90 mb-2 font-medium">
            Your partnership request has been received.
          </p>
          <p className="text-white/70 mb-8">
            Our team will review and respond within 1–2 business days.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-akten hover:bg-akten-dark text-white font-semibold px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center px-4 py-16">
      <div className="max-w-3xl w-full bg-slate-900/80 border border-white/10 rounded-3xl shadow-2xl p-8 md:p-10">
        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-white/60 hover:text-white text-sm mb-6 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          Back to Home
        </Link>

        {/* Header */}
        <div className="mb-8 text-center">
          <p className="text-xs tracking-[0.2em] uppercase text-akten mb-3">
            Partnership Request
          </p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-3">
            Partner with AKTEN INC
          </h1>
          <p className="text-sm md:text-base text-white/70">
            We work with authorized wholesalers, publishers, distributors, brand owners,
            and manufacturers. Share your business details below – we carefully review
            all partnership requests and respond to qualified suppliers.
          </p>
        </div>

        {/* Form */}
        <form
          id="partnership-form"
          onSubmit={handleSubmit}
          className="space-y-6"
        >
          {/* Company Name */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Company Name <span className="text-akten">*</span>
            </label>
            <input
              type="text"
              name="company_name"
              required
              className="w-full rounded-xl bg-slate-950/60 border border-white/10 px-3 py-2 text-sm outline-none focus:border-akten focus:ring-1 focus:ring-akten"
              placeholder="Legal business name"
            />
          </div>

          {/* Website / URL */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Website / Company URL <span className="text-akten">*</span>
            </label>
            <input
              type="text"
              name="website_url"
              required
              className="w-full rounded-xl bg-slate-950/60 border border-white/10 px-3 py-2 text-sm outline-none focus:border-akten focus:ring-1 focus:ring-akten"
              placeholder="www.yourcompany.com"
            />
          </div>

          {/* Contact name + email */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Contact Name <span className="text-akten">*</span>
              </label>
              <input
                type="text"
                name="contact_name"
                required
                className="w-full rounded-xl bg-slate-950/60 border border-white/10 px-3 py-2 text-sm outline-none focus:border-akten focus:ring-1 focus:ring-akten"
                placeholder="Full name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">
                Business Email <span className="text-akten">*</span>
              </label>
              <input
                type="email"
                name="email"
                required
                className="w-full rounded-xl bg-slate-950/60 border border-white/10 px-3 py-2 text-sm outline-none focus:border-akten focus:ring-1 focus:ring-akten"
                placeholder="name@company.com"
              />
            </div>
          </div>

          {/* Business type + volume */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                Business Type <span className="text-akten">*</span>
              </label>
              <select
                name="business_type"
                required
                className="w-full rounded-xl bg-slate-950/60 border border-white/10 px-3 py-2 text-sm outline-none focus:border-akten focus:ring-1 focus:ring-akten"
                defaultValue={preselectedType}
              >
                <option value="" disabled>
                  Select one
                </option>
                <option value="Publisher">Publisher</option>
                <option value="Wholesaler">Wholesaler</option>
                <option value="Distributor">Distributor</option>
                <option value="Brand Owner">Brand Owner</option>
                <option value="Manufacturer">Manufacturer</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">
                Approx. Annual Volume (USD)
              </label>
              <select
                name="annual_volume"
                className="w-full rounded-xl bg-slate-950/60 border border-white/10 px-3 py-2 text-sm outline-none focus:border-akten focus:ring-1 focus:ring-akten"
                defaultValue=""
              >
                <option value="">Select range</option>
                <option value="Under $50k">Under $50k</option>
                <option value="$50k - $250k">$50k – $250k</option>
                <option value="$250k - $1M">$250k – $1M</option>
                <option value="$1M+">$1M+</option>
              </select>
            </div>
          </div>

          {/* Product categories */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Product Categories <span className="text-akten">*</span>
            </label>
            <input
              type="text"
              name="product_categories"
              required
              className="w-full rounded-xl bg-slate-950/60 border border-white/10 px-3 py-2 text-sm outline-none focus:border-akten focus:ring-1 focus:ring-akten"
              placeholder="Example: books, educational materials, media, etc."
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium mb-1">
              Additional Details (optional)
            </label>
            <textarea
              name="additional_details"
              rows={4}
              maxLength={500}
              className="w-full rounded-xl bg-slate-950/60 border border-white/10 px-3 py-2 text-sm outline-none focus:border-akten focus:ring-1 focus:ring-akten resize-none"
              placeholder="Tell us about your catalog, distribution rights, or any relevant details."
            />
          </div>

          {/* Compliance note */}
          <p className="text-xs text-white/50">
            We only work with authorized supply chains. Submitting this form does not
            create any contractual obligation or partnership. All requests are reviewed
            according to our compliance and business-fit criteria.
          </p>

          {/* Error message */}
          {error && (
            <div className="bg-red-500/20 border border-red-500/30 rounded-xl px-4 py-3 text-sm text-red-400">
              {error}
            </div>
          )}

          {/* Submit button */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full inline-flex justify-center items-center rounded-full bg-akten hover:bg-akten-dark disabled:bg-akten/50 text-sm font-semibold px-6 py-3 transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              {isSubmitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Submitting...
                </>
              ) : (
                "Submit Partnership Request"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
